/** The play is presentation state; search criteria use existing dashboard keys. */
export type TaxPlayId = 'rd' | 'capital_allowances' | 'accounts_audit' | 'energy' | 'international_tax';

export interface TaxPlay {
  id: TaxPlayId;
  label: string;
  shortLabel: string;
  description: string;
  icon: string;
}

export const TAX_PLAYS: TaxPlay[] = [
  {
    id: 'rd',
    label: 'R&D and innovation',
    shortLabel: 'R&D',
    description: 'Find evidence of technical projects, investment and the people behind them.',
    icon: 'fa-flask',
  },
  {
    id: 'capital_allowances',
    label: 'Capital allowances',
    shortLabel: 'Property',
    description: 'Find property additions and investment that merit a closer accounts review.',
    icon: 'fa-building',
  },
  {
    id: 'accounts_audit',
    label: 'Accounts and audit',
    shortLabel: 'Accounts',
    description: 'Build a local practice list around scale, accounts and recorded advisers.',
    icon: 'fa-file-invoice',
  },
  {
    id: 'energy',
    label: 'Energy tax',
    shortLabel: 'Energy',
    description: 'Explore energy-intensive operations and process evidence for specialist review.',
    icon: 'fa-bolt',
  },
  {
    id: 'international_tax',
    label: 'International tax',
    shortLabel: 'International',
    description: 'Review overseas activity, trade and margins for a specialist tax conversation.',
    icon: 'fa-globe',
  },
];

/** Existing financial slider definitions are supplied by dashboard initialisation. */
export interface TaxFinancialSlider {
  key: string;
  label: string;
}

export const TAX_COMMON_FINANCIAL_SLIDERS: TaxFinancialSlider[] = [
  { key: 'Revenue Growth Rate', label: 'Revenue growth (%)' },
];

/** EPC totals are licensed with the land title data, not energy/emissions access. */
export const TAX_EPC_SLIDERS: TaxFinancialSlider[] = [
  { key: 'EpcBuildingCountSelected', label: 'EPC building count' },
  { key: 'EpcTotalFloorAreaSelected', label: 'EPC total floor area (m²)' },
  { key: 'EpcMinAssetRatingSelected', label: 'EPC minimum asset rating' },
  { key: 'EpcTotalStandardEmissionsSelected', label: 'EPC standard emissions (tCO₂e)' },
  { key: 'EpcTotalTargetEmissionsSelected', label: 'EPC target emissions (tCO₂e)' },
  { key: 'EpcTotalBuildingEmissionsSelected', label: 'EPC building emissions (tCO₂e)' },
  { key: 'EpcBuildingsWithAirconCountSelected', label: 'EPC buildings with air conditioning' },
];

export const TAX_PLAY_FINANCIAL_SLIDERS: Record<TaxPlayId, TaxFinancialSlider[]> = {
  rd: [
    { key: 'Wages And Salaries', label: 'Wages and salaries (£)' },
    { key: 'Wages And Salaries Growth Rate', label: 'Wages growth (%)' },
    { key: 'Operating Expenses', label: 'Operating expenses (£)' },
    { key: 'Inventory', label: 'Inventory (£)' },
    { key: 'Raw Materials And Consumable', label: 'Raw materials and consumables (£)' },
    { key: 'Raw Materials And Consumable Growth Rate', label: 'Raw materials growth (%)' },
    { key: 'Tax Credit', label: 'Tax credit in accounts (£)' },
    { key: 'ComputerSoftwareIntangibleAssets', label: 'Computer software assets (£)' },
    { key: 'ComputerSoftwareAdditionsIntangibleAssets', label: 'Computer software additions (£)' },
    { key: 'PatentsTrademarksAndLicencesIntangibleAssets', label: 'Patent and licence assets (£)' },
    { key: 'PatentsTrademarksAndLicencesAdditionsIntangibleAssets', label: 'Patent and licence additions (£)' },
    { key: 'Wages And Salaries Absolute Growth', label: 'Wages absolute growth (£)' },
  ],
  capital_allowances: [
    { key: 'LandAndBuildingsTangibleAssets', label: 'Land and buildings assets (£)' },
    { key: 'LandAndBuildingsCostTangibleAssets', label: 'Land and buildings cost (£)' },
    { key: 'PlantAndMachineryTangibleAssets', label: 'Plant and machinery assets (£)' },
    { key: 'PlantAndMachineryCostTangibleAssets', label: 'Plant and machinery cost (£)' },
    { key: 'PlantAndMachineryAdditionsTangibleAssets', label: 'Plant and machinery additions (£)' },
    { key: 'FurnitureAndFittingsTangibleAssets', label: 'Furniture and fittings assets (£)' },
    { key: 'FurnitureAndFittingsAdditionsTangibleAssets', label: 'Furniture and fittings additions (£)' },
    { key: 'ComputerEquipmentTangibleAssets', label: 'Computer equipment assets (£)' },
    { key: 'ComputerEquipmentAdditionsTangibleAssets', label: 'Computer equipment additions (£)' },
    { key: 'LeaseholdAssetsTangibleAssets', label: 'Leasehold assets (£)' },
    { key: 'LeaseholdAssetsAdditionsTangibleAssets', label: 'Leasehold additions (£)' },
    { key: 'AssetsUnderConstructionTangibleAssets', label: 'Assets under construction (£)' },
    { key: 'AssetsUnderConstructionAdditionsTangibleAssets', label: 'Construction additions (£)' },
    { key: 'Fixed Assets', label: 'Fixed assets (£)' },
    { key: 'Title Price Paid', label: 'Land title price paid (£)' },
    { key: 'Ownership Age Months', label: 'Land ownership age (months)' },
  ],
  accounts_audit: [
    { key: 'Total Assets', label: 'Total assets (£)' },
    { key: 'Cost Of Sales', label: 'Cost of sales (£)' },
    { key: 'Interest Payable', label: 'Interest payable (£)' },
    { key: 'Gross Profit', label: 'Gross profit (£)' },
    { key: 'Operating Profit', label: 'Operating profit (£)' },
    { key: 'Ebitda', label: 'EBITDA (£)' },
    { key: 'Profit Before Tax', label: 'Profit before tax (£)' },
    { key: 'Profit After Tax', label: 'Profit after tax (£)' },
    { key: 'Cash At Bank', label: 'Cash at bank (£)' },
    { key: 'Current Assets', label: 'Current assets (£)' },
    { key: 'Working Capital', label: 'Working capital (£)' },
    { key: 'Debtors Due Within One Year', label: 'Debtors due within one year (£)' },
    { key: 'Debtors Due After One Year', label: 'Debtors due after one year (£)' },
    { key: 'Trade Debtors', label: 'Trade debtors (£)' },
    { key: 'Other Debtors', label: 'Other debtors (£)' },
    { key: 'Total Debtors', label: 'Total debtors (£)' },
    { key: 'Creditors Due Within One Year', label: 'Creditors due within one year (£)' },
    { key: 'Creditors Due After One Year', label: 'Creditors due after one year (£)' },
    { key: 'Total Creditors', label: 'Total creditors (£)' },
    { key: 'Dividends', label: 'Dividends (£)' },
    { key: 'Share Premium', label: 'Share premium (£)' },
    { key: 'Total Assets Minus Current Liabilities', label: 'Total assets less current liabilities (£)' },
    { key: 'Capital And Reserves', label: 'Capital and reserves (£)' },
    { key: 'Retained Earnings', label: 'Retained earnings (£)' },
    { key: 'Shareholders Funds', label: 'Shareholders’ funds (£)' },
    { key: 'Gross Margin Percentage', label: 'Gross margin (%)' },
    { key: 'Gross Profit Growth Rate', label: 'Gross profit growth (%)' },
    { key: 'Revenue Absolute Growth', label: 'Revenue absolute growth (£)' },
    { key: 'Fixed Assets Growth Rate', label: 'Fixed asset growth (%)' },
    { key: 'Fixed Assets Absolute Growth', label: 'Fixed asset absolute growth (£)' },
    { key: 'Current Assets Growth Rate', label: 'Current assets growth (%)' },
    { key: 'Cash At Bank Growth Rate', label: 'Cash at bank growth (%)' },
    { key: 'Total Assets Growth Rate', label: 'Asset growth (%)' },
    { key: 'Fx Gain Or Loss', label: 'FX gain or loss (£)' },
  ],
  energy: [
    { key: 'EsosTotalEnergyConsumptionKwh', label: 'ESOS total energy (kWh)' },
    { key: 'EsosPotentialReductionKwh', label: 'ESOS potential saving (kWh)' },
    { key: 'EsosPotentialReductionGbp', label: 'ESOS potential saving (£)' },
  ],
  international_tax: [
    { key: 'Gross Margin Percentage', label: 'Gross margin (%)' },
    { key: 'Fx Gain Or Loss', label: 'FX gain or loss (£)' },
  ],
};

const rangeKeys = (sliders: TaxFinancialSlider[]): string[] =>
  sliders.flatMap(({ key }) => [`lower${key}`, `upper${key}`]);

const financialLabels = (sliders: TaxFinancialSlider[]): Record<string, string> =>
  Object.fromEntries(sliders.flatMap(({ key, label }) => [
    [`lower${key}`, `${label} from`], [`upper${key}`, `${label} to`],
  ]));

export const TAX_COMMON_FILTER_KEYS = [
  'companyNameContains',
  'selectedCompanyStatuses',
  'selectedTerritory',
  'ukRegion',
  'postcodeBeginsWith',
  'selectedSICCodes',
  'excludedSICCodes',
  'selectedAddressTypeOption',
  'postcodeDistrict',
  'postcodeWithinRadius',
  'mileRadiusOfPostcode',
  'lowerAge',
  'upperAge',
  'employeeLowerCount',
  'employeeUpperCount',
  'lowerHeadCountGrowthPercentage',
  'upperHeadCountGrowthPercentage',
  'excludeEstimatedHeadcountFigures',
  'lowerRevenue',
  'upperRevenue',
  'ignoreRevenueEstimates',
  ...rangeKeys(TAX_COMMON_FINANCIAL_SLIDERS),
  'corporateStructureIsTopLevelParentCompany',
  'corporateStructureHasParent',
  'corporateStructureDoesNotHaveParent',
  'corporateStructureHasChild',
  'hasOverseasParent',
  'hasOfficesOverseas',
  'hasWebsite',
  'hasPhoneNumber',
  'hasEmailAddress',
  'accountsSearchTerm',
  'accountsSearchWithinDays',
  'newsContaining',
  'newsWithinDays',
  'newsSource',
  'homepageSearchTerm',
  'fullWebsiteSearchTerm',
  'summarySemanticSearchTerm',
  'hiringJobTitleContains',
  'hiringDescriptionContains',
  'selectedHiringDayLimit',
  'recentDirectorshipTitleContains',
  'lowerRecentDirectorshipMonth',
  'upperRecentDirectorshipMonth',
  'directorJobTitleContains',
  'accountantNameContains',
  'selectedAccountants',
  'selectedCustomColumns',
  'tagPositiveIdsSelected',
  'tagNegativeIdsSelected',
  'excludeCompaniesTaggedByTeam',
  'lowerHeatScore',
  'upperHeatScore',
] as const;

export const TAX_PLAY_FILTER_KEYS: Record<TaxPlayId, readonly string[]> = {
  rd: [
    'hasAnyPatentRecord', 'hasActivePatent', 'patentFiledWithinDays',
    'lowerPatentAwardedDays', 'upperPatentAwardedDays',
    'hasRaisedInvestment', 'lowerInvestmentAmount', 'upperInvestmentAmount',
    'issuedAGrant', 'lowerGrantAmount', 'upperGrantAmount',
    'grantLowerMonthsUntilProjectEnd', 'grantUpperMonthsUntilProjectEnd',
    'similarCompanyUrl',
    ...rangeKeys(TAX_PLAY_FINANCIAL_SLIDERS.rd),
  ],
  capital_allowances: [
    'lowerLandAndBuildingsAdditionsTangibleAssets',
    'upperLandAndBuildingsAdditionsTangibleAssets',
    'lowerCorporation Tax',
    'upperCorporation Tax',
    'upperDaysUntilFinancialYearEnd',
    'hasLandTitle',
    'lowerDaysUntilFinancialYearEnd',
    'landTenure',
    'landCategory',
    ...rangeKeys(TAX_PLAY_FINANCIAL_SLIDERS.capital_allowances),
    ...rangeKeys(TAX_EPC_SLIDERS),
  ],
  accounts_audit: [
    'auditorNameContains',
    'selectedAuditors',
    'bankerNameContains',
    'selectedBankers',
    'noOutstandingCharges',
    'chargeRegistered',
    'outstandingChargeRegistered',
    'chargeHolderNameContains',
    'chargeCreatedMonthsAgoLower', 'chargeCreatedMonthsAgoUpper',
    'ignoreSatisfiedCharges',
    'selectedLatestAccountsAuditOption',
    'accountsAreOverdue',
    'selectedCompanyCategories',
    'lowerAccountsDueSelected', 'upperAccountsDueSelected',
    'lowerLatestAccountsSubmittedSelected', 'upperLatestAccountsSubmittedSelected',
    'selectedLatestAccountsGroupOption',
    'selectedFinancialYearEnds',
    ...rangeKeys(TAX_PLAY_FINANCIAL_SLIDERS.accounts_audit),
  ],
  energy: [
    'lowerTotalEnergyKwhSelected', 'upperTotalEnergyKwhSelected',
    'lowerGasKwhSelected', 'upperGasKwhSelected',
    'lowerElectricKwhSelected', 'upperElectricKwhSelected',
    'isLowEnergy',
    ...rangeKeys(TAX_PLAY_FINANCIAL_SLIDERS.energy),
  ],
  international_tax: [
    'selectedOfficeCountries',
    'lowerGeographicalRevenueSelected', 'upperGeographicalRevenueSelected',
    'selectedGeographicalRevenueTerritories',
    'selectedGeographicalRevenueCountries',
    'selectedImportExportCategories',
    'selectedImportingOrExportingOption',
    'importExportTimeScale',
    ...rangeKeys(TAX_PLAY_FINANCIAL_SLIDERS.international_tax),
  ],
};

export const TAX_FILTER_LABELS: Record<string, string> = {
  companyNameContains: 'Company name',
  selectedCompanyStatuses: 'Company status',
  selectedTerritory: 'Territory',
  ukRegion: 'UK region',
  postcodeBeginsWith: 'Postcode starts with',
  selectedSICCodes: 'Industry',
  excludedSICCodes: 'Excluded industry',
  selectedAddressTypeOption: 'Address type',
  postcodeDistrict: 'Postcode district',
  postcodeWithinRadius: 'Postcode radius centre',
  mileRadiusOfPostcode: 'Radius (miles)',
  lowerAge: 'Company age from',
  upperAge: 'Company age to',
  employeeLowerCount: 'Employees from',
  employeeUpperCount: 'Employees to',
  lowerHeadCountGrowthPercentage: 'Headcount growth from',
  upperHeadCountGrowthPercentage: 'Headcount growth to',
  excludeEstimatedHeadcountFigures: 'Filed headcount only',
  lowerRevenue: 'Revenue from',
  upperRevenue: 'Revenue to',
  ignoreRevenueEstimates: 'Filed revenue only',
  ...financialLabels(TAX_COMMON_FINANCIAL_SLIDERS),
  corporateStructureIsTopLevelParentCompany: 'Top-level parent',
  corporateStructureHasParent: 'Has parent',
  corporateStructureDoesNotHaveParent: 'Standalone / no parent',
  corporateStructureHasChild: 'Has subsidiaries',
  hasOverseasParent: 'Overseas parent',
  hasOfficesOverseas: 'Overseas office',
  hasWebsite: 'Has website',
  hasPhoneNumber: 'Has phone number',
  hasEmailAddress: 'Has email address',
  accountsSearchTerm: 'Accounts mention',
  accountsSearchWithinDays: 'Accounts filed within days',
  newsContaining: 'News mentions',
  newsWithinDays: 'News within days',
  newsSource: 'News source',
  homepageSearchTerm: 'Homepage mentions',
  fullWebsiteSearchTerm: 'Website mentions',
  summarySemanticSearchTerm: 'Product or service',
  hiringJobTitleContains: 'Hiring role',
  hiringDescriptionContains: 'Hiring description',
  selectedHiringDayLimit: 'Hiring posted within days',
  recentDirectorshipTitleContains: 'Recent director role',
  lowerRecentDirectorshipMonth: 'Appointment age from',
  upperRecentDirectorshipMonth: 'Appointment age to',
  directorJobTitleContains: 'Current leadership role',
  accountantNameContains: 'Recorded accountant',
  selectedAccountants: 'Selected accountants',
  selectedCustomColumns: 'Workspace or propensity field',
  tagPositiveIdsSelected: 'Tagged with',
  tagNegativeIdsSelected: 'Not tagged with',
  excludeCompaniesTaggedByTeam: 'Exclude team-tagged companies',
  lowerHeatScore: 'Heat Score from',
  upperHeatScore: 'Heat Score to',
  hasAnyPatentRecord: 'Patent record',
  hasActivePatent: 'Active patent',
  patentFiledWithinDays: 'Patent filed within days',
  lowerPatentAwardedDays: 'Patent awarded from days ago',
  upperPatentAwardedDays: 'Patent awarded to days ago',
  hasRaisedInvestment: 'Raised investment',
  lowerInvestmentAmount: 'Investment raised from',
  upperInvestmentAmount: 'Investment raised to',
  issuedAGrant: 'Grant awarded',
  lowerGrantAmount: 'Grant amount from',
  upperGrantAmount: 'Grant amount to',
  grantLowerMonthsUntilProjectEnd: 'Grant project ends from',
  grantUpperMonthsUntilProjectEnd: 'Grant project ends to',
  similarCompanyUrl: 'Similar company',
  ...financialLabels(TAX_PLAY_FINANCIAL_SLIDERS.rd),
  lowerLandAndBuildingsAdditionsTangibleAssets: 'Property additions from',
  upperLandAndBuildingsAdditionsTangibleAssets: 'Property additions to',
  'lowerCorporation Tax': 'Corporation tax from',
  'upperCorporation Tax': 'Corporation tax to',
  upperDaysUntilFinancialYearEnd: 'Next year end within days',
  hasLandTitle: 'Land title',
  lowerDaysUntilFinancialYearEnd: 'Next year end from days',
  landTenure: 'Land tenure',
  landCategory: 'Land category',
  ...financialLabels(TAX_PLAY_FINANCIAL_SLIDERS.capital_allowances),
  ...financialLabels(TAX_EPC_SLIDERS),
  auditorNameContains: 'Recorded auditor',
  selectedAuditors: 'Selected auditors',
  bankerNameContains: 'Recorded banker',
  selectedBankers: 'Selected bankers',
  noOutstandingCharges: 'No outstanding charges',
  chargeRegistered: 'Has a registered charge',
  outstandingChargeRegistered: 'Has an outstanding charge',
  chargeHolderNameContains: 'Charge holder name',
  chargeCreatedMonthsAgoLower: 'Charge created from months ago',
  chargeCreatedMonthsAgoUpper: 'Charge created to months ago',
  ignoreSatisfiedCharges: 'Ignore satisfied charges',
  selectedLatestAccountsAuditOption: 'Latest audit status',
  accountsAreOverdue: 'Accounts overdue',
  selectedCompanyCategories: 'Company category',
  lowerAccountsDueSelected: 'Accounts due from days',
  upperAccountsDueSelected: 'Accounts due to days',
  lowerLatestAccountsSubmittedSelected: 'Latest accounts filed from days',
  upperLatestAccountsSubmittedSelected: 'Latest accounts filed to days',
  selectedLatestAccountsGroupOption: 'Accounts type',
  selectedFinancialYearEnds: 'Year-end month',
  ...financialLabels(TAX_PLAY_FINANCIAL_SLIDERS.accounts_audit),
  lowerTotalEnergyKwhSelected: 'Energy use from',
  upperTotalEnergyKwhSelected: 'Energy use to',
  lowerGasKwhSelected: 'Gas use from',
  upperGasKwhSelected: 'Gas use to',
  lowerElectricKwhSelected: 'Electricity use from',
  upperElectricKwhSelected: 'Electricity use to',
  isLowEnergy: 'Reported low energy use',
  ...financialLabels(TAX_PLAY_FINANCIAL_SLIDERS.energy),
  selectedOfficeCountries: 'Office countries',
  lowerGeographicalRevenueSelected: 'Overseas revenue from',
  upperGeographicalRevenueSelected: 'Overseas revenue to',
  selectedGeographicalRevenueTerritories: 'Overseas revenue territories',
  selectedGeographicalRevenueCountries: 'Overseas revenue countries',
  selectedImportExportCategories: 'Trade product categories',
  selectedImportingOrExportingOption: 'Import or export direction',
  importExportTimeScale: 'Trade activity within months',
  ...financialLabels(TAX_PLAY_FINANCIAL_SLIDERS.international_tax),
};

// These conventional filters have separate plan checks. PAF URL criteria are
// restored only after access is confirmed; the other gated URL criteria still
// open in Full, where their existing controls and permission checks apply.
export const TAX_PAF_FILTER_KEYS = new Set([
  'hasLandTitle',
  'landTenure',
  'landCategory',
  'lowerTitle Price Paid',
  'upperTitle Price Paid',
  'lowerOwnership Age Months',
  'upperOwnership Age Months',
  ...rangeKeys(TAX_EPC_SLIDERS),
]);

const TAX_PERMISSION_GATED_URL_KEYS = new Set([
  'hasAnyPatentRecord',
  'hasActivePatent',
  'patentFiledWithinDays',
  'lowerPatentAwardedDays',
  'upperPatentAwardedDays',
  'noOutstandingCharges',
  'chargeRegistered',
  'outstandingChargeRegistered',
  'chargeHolderNameContains',
  'chargeCreatedMonthsAgoLower',
  'chargeCreatedMonthsAgoUpper',
  'ignoreSatisfiedCharges',
  ...TAX_PAF_FILTER_KEYS,
  'lowerTotalEnergyKwhSelected',
  'upperTotalEnergyKwhSelected',
  'lowerGasKwhSelected',
  'upperGasKwhSelected',
  'lowerElectricKwhSelected',
  'upperElectricKwhSelected',
  'isLowEnergy',
  ...rangeKeys(TAX_PLAY_FINANCIAL_SLIDERS.energy),
]);

export interface TaxFilterTag {
  param: string;
  value: unknown;
}

export function taxVisibleFilterKeys(play: TaxPlayId): Set<string> {
  return new Set([...TAX_COMMON_FILTER_KEYS, ...TAX_PLAY_FILTER_KEYS[play]]);
}

/** The grouped Tax workspace shows every service section together. */
export function taxWorkspaceFilterKeys(): Set<string> {
  return new Set([
    ...TAX_COMMON_FILTER_KEYS,
    ...Object.values(TAX_PLAY_FILTER_KEYS).flat(),
  ]);
}

export function isActiveTaxFilterTag(tag: TaxFilterTag | null | undefined): boolean {
  if (!tag || !(tag.param in TAX_FILTER_LABELS)) return false;
  if (Array.isArray(tag.value)) return tag.value.length > 0;
  if (typeof tag.value === 'string' && !tag.value.trim()) return false;
  return ![
    null, undefined, '', false, 'false', 'null', 'undefined', '[]', '{}',
  ].includes(tag.value as never);
}

/** A shared URL may only restore fields that the grouped workspace shows. */
export function unsupportedTaxQueryKeys(
  params: Record<string, unknown>,
  _play: TaxPlayId,
  hasPAFAccess = false
): string[] {
  const visible = taxWorkspaceFilterKeys();
  return Object.keys(params).filter(key =>
    key !== 'play' && key !== 'resultsMode' && key !== 'pageNumber' &&
    (!visible.has(key) ||
      (TAX_PERMISSION_GATED_URL_KEYS.has(key) &&
        !(hasPAFAccess && TAX_PAF_FILTER_KEYS.has(key))))
  );
}

export function inferTaxPlay(params: Record<string, unknown>): TaxPlayId | null {
  const matched = (Object.keys(TAX_PLAY_FILTER_KEYS) as TaxPlayId[])
    .filter(play => TAX_PLAY_FILTER_KEYS[play].some(key => key in params));
  if (matched.length <= 1) return matched[0] || 'rd';
  // Gross margin and FX measures already belong to Accounts and audit. A
  // specialist international criterion resolves the shared measure to the new
  // play; URLs containing genuinely different specialist plays remain invalid.
  const exclusive = matched.filter(play =>
    TAX_PLAY_FILTER_KEYS[play].some(key => key in params &&
      matched.every(other => other === play || !TAX_PLAY_FILTER_KEYS[other].includes(key))));
  if (exclusive.length === 1) return exclusive[0];
  if (exclusive.length === 0 && matched.every(play =>
    play === 'accounts_audit' || play === 'international_tax')) return 'accounts_audit';
  return null;
}
